import { Link } from "react-router-dom";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import ParallaxImage from "@/components/site/ParallaxImage";
import AnimatedBackground from "@/components/site/AnimatedBackground";
import { useContent } from "@/context/ContentContext";
import { ArrowUpRight } from "lucide-react";

export default function About() {
  const { content } = useContent();
  const A = content.about;
  return (
    <div data-testid="about-page" className="relative pt-32">
      <AnimatedBackground variant="editorial" />
      {/* Company intro */}
      <section className="mx-auto max-w-[1400px] px-6 pb-24 lg:px-12">
        <p className="font-mono text-sm uppercase tracking-[0.3em] text-brand-blue">{A.label}</p>
        <h1 className="mt-6 max-w-5xl font-display text-5xl font-medium leading-[0.95] tracking-tight text-white sm:text-7xl">
          <MaskedLines lines={[A.headingLine1, A.headingLine2]} />
          <MaskedLines lines={[A.headingLine3]} delay={0.25} lineClassName="text-brand-gradient" />
        </h1>
        <div className="mt-14 grid gap-12 border-t border-white/10 pt-12 md:grid-cols-12">
          <Reveal className="md:col-span-7 md:col-start-6">
            <p className="text-xl font-light leading-relaxed text-slate-300">
              {A.companyText1}
            </p>
            <p className="mt-6 text-lg font-light leading-relaxed text-slate-400">
              {A.companyText2}
            </p>
          </Reveal>
        </div>
      </section>

      {/* About me */}
      <section className="border-y border-white/10 bg-surface py-24" data-testid="about-me-section">
        <div className="mx-auto grid max-w-[1400px] items-center gap-16 px-6 lg:grid-cols-12 lg:px-12">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <ParallaxImage
                src={A.portraitUrl}
                alt="Founder portrait"
                duotone
                className="aspect-[4/5] w-full"
                strength={40}
              />
              <span className="absolute left-4 top-4 font-mono text-sm uppercase tracking-[0.2em] text-white/70">
                {A.portraitCaption}
              </span>
            </div>
          </Reveal>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <p className="font-mono text-sm uppercase tracking-[0.3em] text-brand-magenta">{A.meLabel}</p>
              <h2 className="mt-6 font-display text-4xl font-medium tracking-tight text-white sm:text-5xl">
                {A.meHeadingPre} <span className="text-brand-gradient">{A.meHeadingHighlight}</span>
              </h2>
              <p className="mt-8 text-lg font-light leading-relaxed text-slate-300">
                {A.meText1}
              </p>
              <p className="mt-5 text-lg font-light leading-relaxed text-slate-400">
                {A.meText2}
              </p>

              <div className="mt-12 grid grid-cols-3 gap-px border border-white/10 bg-white/10">
                {(A.stats || []).map((s, i) => (
                  <div key={i} className="bg-surface p-6">
                    <p className={`font-display text-3xl font-semibold sm:text-4xl ${i === 1 ? "text-brand-magenta" : "text-brand-blue"}`}>{s.k}</p>
                    <p className="mt-2 font-mono text-sm uppercase tracking-[0.15em] text-slate-400">{s.v}</p>
                  </div>
                ))}
              </div>

              <Link
                to="/contact"
                data-testid="about-contact-cta"
                className="brand-glow-blue group mt-10 inline-flex items-center gap-2 bg-brand-blue px-6 py-3 font-mono text-sm uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-magenta"
              >
                Work with me
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
